import type { HeartbeatRun, HeartbeatRunEvent, RunInspectionHeader, RunSummary, RunSummaryPage } from "@rudderhq/shared";
import { Command } from "commander";
import { getAgentCliCapabilityById } from "../../agent-v1-registry.js";
import {
  addCommonClientOptions,
  formatCliAgentId,
  formatCliRunId,
  handleCommandError,
  printOutput,
  resolveCommandContext,
  type BaseClientOptions,
} from "./common.js";
import { formatExamplesAndCautions } from "./help.js";

interface RunsCreateOptions extends BaseClientOptions {
  task: string;
  targetAgentId?: string;
  idempotencyKey: string;
}

interface RunsListOptions extends BaseClientOptions {
  updatedAfter?: string;
  runIdPrefix?: string;
  agentId?: string;
  status?: string;
  runtime?: string;
  issueId?: string;
  usedSkill?: string;
  loadedSkill?: string;
  createdBefore?: string;
  cursor?: string;
  limit?: string;
  full?: boolean;
}

interface RunsBySkillOptions extends BaseClientOptions {
  evidence?: string;
  agentId?: string;
  status?: string;
  runtime?: string;
  issueId?: string;
  createdBefore?: string;
  cursor?: string;
  limit?: string;
  full?: boolean;
}

interface RunLogOptions extends BaseClientOptions {
  maxChars?: string;
  offset?: string;
  limitBytes?: string;
}

interface RunEventsOptions extends BaseClientOptions {
  afterSeq?: string;
  cursor?: string;
  limit?: string;
  maxChars?: string;
  full?: boolean;
}

interface RunTranscriptOptions extends BaseClientOptions {
  errorsOnly?: boolean;
  aroundError?: string;
  contextTurns?: string;
  cursor?: string;
  turnLimit?: string;
  chronological?: boolean;
  narrative?: boolean;
  maxChars?: string;
  maxOutputChars?: string;
  includeOutput?: boolean;
  includeOutputs?: boolean;
  full?: boolean;
}

interface RunErrorsOptions extends BaseClientOptions {
  maxChars?: string;
  cursor?: string;
}

interface RunGetOptions extends BaseClientOptions {
  full?: boolean;
}

interface RunExportRow {
  run: HeartbeatRun;
  agentName: string | null;
  orgName: string | null;
  issue: { id: string; identifier: string | null; title: string | null } | null;
  bundle: { agentRuntimeType: string };
  errorSummary?: string | null;
  skillEvidence?: {
    evidenceType: "used" | "loaded";
    matchedSkillKey: string;
    matchedSkillLabel: string | null;
    sourceEventType: string | null;
    sourceEventId: number | null;
    sourceEventCreatedAt: string | null;
  } | null;
}

interface SkillRunReport<Row extends RunListRow = RunListRow> {
  skill: {
    query: string;
    evidenceType: "used" | "loaded";
  };
  summary: {
    total: number;
    succeeded: number;
    failed: number;
    cancelled: number;
    timedOut: number;
    running: number;
    queued: number;
    other: number;
    agents: Array<{ id: string; name: string | null; count: number }>;
    issues: Array<{ id: string; identifier: string | null; title: string | null; count: number }>;
    commonErrors: Array<{ summary: string; count: number }>;
  };
  rows: Row[];
  page?: RunSummaryPage["page"];
  nextCommands: string[];
}

type RunListRow = RunExportRow | RunSummary;

interface RunTranscriptRow {
  id: string;
  index: number;
  turnIndex: number | null;
  kind: string;
  ts: string;
  label: string;
  preview: string;
  detailPreview: string;
  isError: boolean;
  output: {
    text: string;
    clipped: boolean;
    originalLength: number;
  } | null;
}

interface RunTranscriptEntry {
  id: string;
  index: number;
  turnIndex: number | null;
  entry: unknown;
  output: {
    text: string;
    clipped: false;
    originalLength: number;
  };
}

interface RunTranscriptResponse {
  run: HeartbeatRun | RunInspectionHeader;
  agentName: string | null;
  orgName: string | null;
  issue: RunExportRow["issue"];
  order: "newest" | "oldest";
  output: "compact" | "full";
  page: {
    cursor: string | null;
    nextCursor: string | null;
    hasMore: boolean;
    order: "newest" | "oldest";
    turnLimit: number | null;
    returnedSteps: number;
    totalFilteredSteps: number;
  };
  rows: RunTranscriptRow[];
  entries?: RunTranscriptEntry[];
  transcript?: unknown[];
  trace: {
    turnCount: number;
    stepCount: number;
    payloadStepCount: number;
    filteredStepCount?: number;
  };
}

interface RunErrorRow {
  id: string;
  type: string;
  index: number | null;
  turnIndex: number | null;
  ts: string | null;
  summary: string;
  output: {
    text: string;
    clipped: boolean;
    originalLength: number;
  };
  transcriptContext: {
    id: string;
    command: string;
  } | null;
}

interface RunErrorsResponse {
  run: HeartbeatRun;
  agentName: string | null;
  orgName: string | null;
  issue: RunExportRow["issue"];
  errors: RunErrorRow[];
}

export function registerRunsCommands(program: Command): void {
  const runs = program.command("runs").description("Run debugging operations");

  addCommonClientOptions(
    runs
      .command("create")
      .description(getAgentCliCapabilityById("runs.create").description)
      .requiredOption("--task <text>", "Delegation task text")
      .option("--target-agent-id <id>", "Same-organization target Agent; defaults to the current Agent")
      .requiredOption("--idempotency-key <key>", "Stable key for safe replay")
      .action(async (opts: RunsCreateOptions) => {
        try {
          const ctx = resolveCommandContext(opts);
          const row = await ctx.api.post("/api/agent-runs/delegation", {
            task: opts.task,
            ...(opts.targetAgentId ? { targetAgentId: opts.targetAgentId } : {}),
            idempotencyKey: opts.idempotencyKey,
          });
          printOutput(row, { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
  );

  addCommonClientOptions(
    runs
      .command("list")
      .description(getAgentCliCapabilityById("runs.list").description)
      .option("-O, --org-id <id>", "Organization ID")
      .option("--updated-after <iso>", "Only runs updated after this timestamp")
      .option("--run-id-prefix <prefix>", "Filter by run ID prefix")
      .option("--agent-id <id>", "Filter by agent ID")
      .option("--status <status>", "Filter by run status")
      .option("--runtime <type>", "Filter by runtime type")
      .option("--issue-id <id>", "Filter by linked issue ID")
      .option("--used-skill <key-or-name>", "Filter by skill actually used during the run")
      .option("--loaded-skill <key-or-name>", "Filter by skill loaded for the run")
      .option("--created-before <iso>", "Only runs created before this timestamp")
      .option("--cursor <cursor>", "Stable summary cursor returned in page.nextCursor")
      .option("--limit <n>", "Maximum rows; defaults to 50 summaries or 200 full rows")
      .option("--full", "Use the legacy full-row list response for compatibility")
      .addHelpText("after", formatExamplesAndCautions({
        examples: [
          {
            description: "Find recent failures for one agent before opening transcripts:",
            command: "rudder runs list --org-id <org-id> --agent-id <agent-id> --status failed --limit 20",
          },
          {
            description: "Collect skill-specific evidence for a linked issue:",
            command: "rudder runs list --org-id <org-id> --issue-id ZST-123 --used-skill release-maintainer --json",
          },
        ],
        cautions: [
          "Filter first by org, agent, issue, status, skill, or time; do not start by dumping broad run history.",
          "Use runs errors or runs transcript for detail instead of expecting list to return full debug payloads.",
        ],
      }))
      .action(async (opts: RunsListOptions) => {
        try {
          assertSingleSkillFilter(opts);
          const ctx = resolveCommandContext(opts, { requireCompany: true });
          if (opts.full) {
            const rows = (await ctx.api.get<RunExportRow[]>(`/api/run-intelligence/orgs/${ctx.orgId}/runs?${buildRunsListQuery(opts)}`)) ?? [];
            printOutput(ctx.json ? rows : rows.map(formatRunListRow), { json: ctx.json });
            return;
          }
          const page = await ctx.api.get<RunSummaryPage>(`/api/run-intelligence/orgs/${ctx.orgId}/runs?${buildRunsListQuery(opts)}`);
          printOutput(ctx.json ? page : formatRunSummaryPage(page), { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
    { includeCompany: false },
  );

  addCommonClientOptions(
    runs
      .command("by-skill")
      .description(getAgentCliCapabilityById("runs.by-skill").description)
      .argument("<skill>", "Skill key or display name")
      .option("-O, --org-id <id>", "Organization ID")
      .option("--evidence <used|loaded>", "Evidence type to match; defaults to used", "used")
      .option("--agent-id <id>", "Filter by agent ID")
      .option("--status <status>", "Filter by run status")
      .option("--runtime <type>", "Filter by runtime type")
      .option("--issue-id <id>", "Filter by linked issue ID")
      .option("--created-before <iso>", "Only runs created before this timestamp")
      .option("--cursor <cursor>", "Stable summary cursor returned in page.nextCursor")
      .option("--limit <n>", "Maximum rows", "50")
      .option("--full", "Use the legacy full-row list response for compatibility")
      .action(async (skill: string, opts: RunsBySkillOptions) => {
        try {
          const evidenceType = parseSkillEvidenceType(opts.evidence);
          const ctx = resolveCommandContext(opts, { requireCompany: true });
          const params = buildRunsBySkillQuery(skill, opts, evidenceType);
          if (opts.full) {
            const rows = (await ctx.api.get<RunExportRow[]>(`/api/run-intelligence/orgs/${ctx.orgId}/runs?${params}`)) ?? [];
            const report = buildSkillRunReport(skill, evidenceType, rows);
            printOutput(ctx.json ? report : formatSkillRunReport(report), { json: ctx.json });
            return;
          }
          const page = await ctx.api.get<RunSummaryPage>(`/api/run-intelligence/orgs/${ctx.orgId}/runs?${params}`);
          const report = buildSkillRunReport(skill, evidenceType, page?.items ?? [], page?.page);
          printOutput(ctx.json ? report : formatSkillRunReport(report), { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
    { includeCompany: false },
  );

  addCommonClientOptions(
    runs
      .command("get")
      .description(getAgentCliCapabilityById("runs.get").description)
      .argument("<runId>", "Run ID or short run ID")
      .option("--full", "Include raw run result, context, excerpts, and session fields")
      .action(async (runId: string, opts: RunGetOptions) => {
        try {
          const ctx = resolveCommandContext(opts);
          const projection = opts.full ? "full" : "summary";
          const row = await ctx.api.get<RunExportRow | RunSummary>(
            `/api/run-intelligence/runs/${encodeURIComponent(runId)}?projection=${projection}`,
          );
          printOutput(row, { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
  );

  addCommonClientOptions(
    runs
      .command("events")
      .description(getAgentCliCapabilityById("runs.events").description)
      .argument("<runId>", "Run ID or short run ID")
      .option("--cursor <cursor>", "Opaque total-order cursor returned in page.nextCursor")
      .option("--after-seq <n>", "Legacy sequence-only cursor; prefer --cursor", "0")
      .option("--limit <n>", "Maximum events to return", "200")
      .option("--max-chars <n>", "Maximum payload preview characters per event", "1200")
      .option("--full", "Include raw event payloads; unavailable through MCP")
      .action(async (runId: string, opts: RunEventsOptions) => {
        try {
          const ctx = resolveCommandContext(opts);
          const params = new URLSearchParams({
            afterSeq: String(parseNonNegativeInteger(opts.afterSeq, 0)),
            limit: String(parseLimit(opts.limit, 200)),
            maxChars: String(parseLimit(opts.maxChars, 1200)),
            projection: opts.full ? "full" : "compact",
          });
          if (opts.cursor) params.set("cursor", opts.cursor);
          const page = await ctx.api.get<{ items: HeartbeatRunEvent[]; page: Record<string, unknown> }>(
            `/api/run-intelligence/runs/${encodeURIComponent(runId)}/events?${params}`,
          );
          printOutput(ctx.json ? page : (page?.items ?? []).map(formatRunEvent), { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
  );

  addCommonClientOptions(
    runs
      .command("log")
      .description(getAgentCliCapabilityById("runs.log").description)
      .argument("<runId>", "Run ID or short run ID")
      .option("--max-chars <n>", "Maximum log characters for human output", "12000")
      .option("--offset <n>", "Start reading at this byte offset", "0")
      .option("--limit-bytes <n>", "Maximum log bytes to return", "256000")
      .action(async (runId: string, opts: RunLogOptions) => {
        try {
          const ctx = resolveCommandContext(opts);
          const params = new URLSearchParams({
            offset: String(parseNonNegativeInteger(opts.offset, 0)),
            limitBytes: String(parseLimit(opts.limitBytes, 256000)),
          });
          const row = await ctx.api.get<{ content: string }>(
            `/api/run-intelligence/runs/${encodeURIComponent(runId)}/log?${params}`,
          );
          if (ctx.json) {
            printOutput(row, { json: true });
          } else {
            process.stdout.write(clip(row?.content ?? "", parseLimit(opts.maxChars, 12000)) + "\n");
          }
        } catch (err) {
          handleCommandError(err);
        }
      }),
  );

  addCommonClientOptions(
    runs
      .command("transcript")
      .description(getAgentCliCapabilityById("runs.transcript").description)
      .argument("<runId>", "Run ID or short run ID")
      .option("--errors-only", "Show only error transcript rows")
      .option("--around-error <id>", "Show context around a run error id such as step-12")
      .option("--context-turns <n>", "Turns around --around-error", "1")
      .option("--cursor <cursor>", "Stable transcript cursor returned in page.nextCursor")
      .option("--turn-limit <n>", "Maximum turns to return", "20")
      .option("--chronological", "Show oldest-first instead of default newest-first")
      .option("--narrative", "Use a narrative human layout")
      .option("--max-chars <n>", "Maximum output characters per row", "1200")
      .option("--max-output-chars <n>", "Alias for --max-chars")
      .option("--include-output", "Include row output in compact human transcript rows")
      .option("--include-outputs", "Alias for --include-output")
      .option("--full", "Return raw lossless transcript entries; unavailable through MCP")
      .addHelpText("after", formatExamplesAndCautions({
        examples: [
          {
            description: "Inspect the neighborhood around a failing step:",
            command: "rudder runs transcript <run-id> --around-error step-12 --context-turns 2",
          },
          {
            description: "Reconstruct a bounded chronological decision trail:",
            command: "rudder runs transcript <run-id> --chronological --turn-limit 30 --include-output",
          },
        ],
        cautions: [
          "Human and JSON output use the same compact projection; --json changes encoding only.",
          "Use --full only from a direct trusted CLI when lossless provider entries are required.",
          "Use --around-error from runs errors when investigating a failure instead of reading the entire run first.",
        ],
      }))
      .action(async (runId: string, opts: RunTranscriptOptions) => {
        try {
          const ctx = resolveCommandContext(opts);
          const payload = await ctx.api.get<RunTranscriptResponse>(
            `/api/run-intelligence/runs/${encodeURIComponent(runId)}/transcript?${buildTranscriptQuery(opts)}`,
          );
          if (ctx.json) {
            printOutput(payload, { json: true });
          } else if (opts.narrative) {
            printOutput((payload?.rows ?? []).map(formatRunTranscriptNarrative), { json: false });
          } else {
            printOutput((payload?.rows ?? []).map(formatRunTranscriptRow), { json: false });
          }
        } catch (err) {
          handleCommandError(err);
        }
      }),
  );

  addCommonClientOptions(
    runs
      .command("errors")
      .description(getAgentCliCapabilityById("runs.errors").description)
      .argument("<runId>", "Run ID or short run ID")
      .option("--max-chars <n>", "Maximum output characters per error", "1200")
      .option("--cursor <cursor>", "Cursor returned in page.nextCursor")
      .addHelpText("after", formatExamplesAndCautions({
        examples: [
          {
            description: "Start failed-run investigation with error summaries:",
            command: "rudder runs errors <run-id>",
          },
          {
            description: "Increase clipped error output for scripts or reports:",
            command: "rudder runs errors <run-id> --max-chars 4000 --json",
          },
        ],
        cautions: [
          "Start here for failed runs, then follow the transcript context command returned for the relevant step.",
          "Increase --max-chars deliberately; large tool outputs can be noisy and expensive to inspect.",
        ],
      }))
      .action(async (runId: string, opts: RunErrorsOptions) => {
        try {
          const ctx = resolveCommandContext(opts);
          const params = new URLSearchParams();
          params.set("maxChars", String(parseLimit(opts.maxChars, 1200)));
          if (opts.cursor) params.set("cursor", opts.cursor);
          const payload = await ctx.api.get<RunErrorsResponse>(
            `/api/run-intelligence/runs/${encodeURIComponent(runId)}/errors?${params.toString()}`,
          );
          printOutput(ctx.json ? payload : (payload?.errors ?? []).map(formatRunError), { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
  );

  addCommonClientOptions(
    runs
      .command("cancel")
      .description(getAgentCliCapabilityById("runs.cancel").description)
      .argument("<runId>", "Run ID or short run ID")
      .action(async (runId: string, opts: BaseClientOptions) => {
        try {
          const ctx = resolveCommandContext(opts);
          const row = await ctx.api.post<HeartbeatRun>(`/api/heartbeat-runs/${encodeURIComponent(runId)}/cancel`, {});
          printOutput(row, { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
  );

  addCommonClientOptions(
    runs
      .command("retry")
      .description(getAgentCliCapabilityById("runs.retry").description)
      .argument("<runId>", "Run ID or short run ID")
      .action(async (runId: string, opts: BaseClientOptions) => {
        try {
          const ctx = resolveCommandContext(opts);
          const row = await ctx.api.post<HeartbeatRun>(`/api/heartbeat-runs/${encodeURIComponent(runId)}/retry`, {});
          printOutput(row, { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
  );
}

function buildRunsListQuery(opts: RunsListOptions) {
  const params = new URLSearchParams();
  params.set("projection", opts.full ? "full" : "summary");
  if (opts.updatedAfter) params.set("updatedAfter", opts.updatedAfter);
  if (opts.runIdPrefix) params.set("runIdPrefix", opts.runIdPrefix);
  if (opts.agentId) params.set("agentId", opts.agentId);
  if (opts.status) params.set("status", opts.status);
  if (opts.runtime) params.set("runtime", opts.runtime);
  if (opts.issueId) params.set("issueId", opts.issueId);
  if (opts.usedSkill) params.set("usedSkill", opts.usedSkill);
  if (opts.loadedSkill) params.set("loadedSkill", opts.loadedSkill);
  if (opts.createdBefore) params.set("createdBefore", opts.createdBefore);
  if (!opts.full && opts.cursor) params.set("cursor", opts.cursor);
  params.set("limit", opts.limit ?? (opts.full ? "200" : "50"));
  return params.toString();
}

function buildRunsBySkillQuery(skill: string, opts: RunsBySkillOptions, evidenceType: "used" | "loaded") {
  const params = new URLSearchParams();
  params.set("projection", opts.full ? "full" : "summary");
  params.set(evidenceType === "used" ? "usedSkill" : "loadedSkill", skill);
  if (opts.agentId) params.set("agentId", opts.agentId);
  if (opts.status) params.set("status", opts.status);
  if (opts.runtime) params.set("runtime", opts.runtime);
  if (opts.issueId) params.set("issueId", opts.issueId);
  if (opts.createdBefore) params.set("createdBefore", opts.createdBefore);
  if (!opts.full && opts.cursor) params.set("cursor", opts.cursor);
  params.set("limit", opts.limit ?? "50");
  return params.toString();
}

function assertSingleSkillFilter(opts: RunsListOptions) {
  if (opts.usedSkill && opts.loadedSkill) {
    throw new Error("Use either --used-skill or --loaded-skill, not both.");
  }
}

function parseSkillEvidenceType(value: string | undefined): "used" | "loaded" {
  const normalized = (value ?? "used").trim().toLowerCase();
  if (normalized === "used" || normalized === "loaded") return normalized;
  throw new Error("--evidence must be either 'used' or 'loaded'.");
}

function buildTranscriptQuery(opts: RunTranscriptOptions) {
  const params = new URLSearchParams();
  if (opts.errorsOnly) params.set("errorsOnly", "true");
  if (opts.aroundError) params.set("aroundError", opts.aroundError);
  if (opts.cursor) params.set("cursor", opts.cursor);
  if (opts.turnLimit) params.set("turnLimit", String(parseLimit(opts.turnLimit, 20)));
  params.set("contextTurns", String(parseLimit(opts.contextTurns, 1)));
  params.set("order", opts.chronological || opts.narrative ? "oldest" : "newest");
  params.set("output", opts.full ? "full" : "compact");
  const includeOutputs = Boolean(opts.full || opts.includeOutput || opts.includeOutputs || opts.narrative);
  params.set("includeOutputs", includeOutputs ? "true" : "false");
  params.set("maxChars", String(parseLimit(opts.maxOutputChars ?? opts.maxChars, 1200)));
  return params.toString();
}

function formatRunListRow(row: RunListRow) {
  const runId = formatCliRunId(runIdOf(row));
  return {
    id: runId,
    status: runStatusOf(row),
    agent: row.agentName ?? runAgentIdOf(row),
    runtime: runRuntimeOf(row),
    issue: formatIssueRef(row.issue),
    createdAt: runCreatedAtOf(row),
    finishedAt: runFinishedAtOf(row) ?? "-",
    evidence: row.skillEvidence?.evidenceType ?? "-",
    skill: row.skillEvidence?.matchedSkillKey ?? "-",
    error: runErrorOf(row) ?? "-",
    next: runStatusOf(row) === "failed" ? `rudder runs errors ${runId}` : `rudder runs transcript ${runId}`,
  };
}

function buildSkillRunReport<Row extends RunListRow>(
  skill: string,
  evidenceType: "used" | "loaded",
  rows: Row[],
  page?: RunSummaryPage["page"],
): SkillRunReport<Row> {
  const statusCounts = {
    succeeded: 0,
    failed: 0,
    cancelled: 0,
    timedOut: 0,
    running: 0,
    queued: 0,
    other: 0,
  };
  const agents = new Map<string, { id: string; name: string | null; count: number }>();
  const issues = new Map<string, { id: string; identifier: string | null; title: string | null; count: number }>();
  const errors = new Map<string, number>();

  for (const row of rows) {
    const status = runStatusOf(row);
    if (status === "succeeded") statusCounts.succeeded += 1;
    else if (status === "failed") statusCounts.failed += 1;
    else if (status === "cancelled") statusCounts.cancelled += 1;
    else if (status === "timed_out") statusCounts.timedOut += 1;
    else if (status === "running") statusCounts.running += 1;
    else if (status === "queued") statusCounts.queued += 1;
    else statusCounts.other += 1;

    const agentId = runAgentIdOf(row);
    const agent = agents.get(agentId) ?? { id: agentId, name: row.agentName, count: 0 };
    agent.count += 1;
    agents.set(agent.id, agent);

    if (row.issue) {
      const issue = issues.get(row.issue.id) ?? { ...row.issue, count: 0 };
      issue.count += 1;
      issues.set(issue.id, issue);
    }

    const error = runErrorOf(row)?.trim();
    if (error) errors.set(error, (errors.get(error) ?? 0) + 1);
  }

  return {
    skill: { query: skill, evidenceType },
    summary: {
      total: rows.length,
      ...statusCounts,
      agents: [...agents.values()].sort((a, b) => b.count - a.count),
      issues: [...issues.values()].sort((a, b) => b.count - a.count),
      commonErrors: [...errors.entries()]
        .map(([summary, count]) => ({ summary, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5),
    },
    rows,
    ...(page ? { page } : {}),
    nextCommands: rows.slice(0, 5).map((row) => {
      const runId = formatCliRunId(runIdOf(row));
      return runStatusOf(row) === "failed"
        ? `rudder runs errors ${runId}`
        : `rudder runs transcript ${runId}`;
    }),
  };
}

function formatSkillRunReport(report: SkillRunReport) {
  const lines = [
    `skill=${report.skill.query} evidence=${report.skill.evidenceType} total=${report.summary.total} succeeded=${report.summary.succeeded} failed=${report.summary.failed} cancelled=${report.summary.cancelled} timedOut=${report.summary.timedOut}`,
  ];
  if (report.summary.agents.length > 0) {
    lines.push(`agents=${report.summary.agents.map((agent) => `${agent.name ?? formatCliAgentId(agent.id)}:${agent.count}`).join(", ")}`);
  }
  if (report.summary.issues.length > 0) {
    lines.push(`issues=${report.summary.issues.map((issue) => `${issue.identifier ?? issue.id}:${issue.count}`).join(", ")}`);
  }
  if (report.summary.commonErrors.length > 0) {
    lines.push(`commonErrors=${report.summary.commonErrors.map((error) => `${clip(error.summary, 80)}:${error.count}`).join(" | ")}`);
  }
  lines.push(...report.rows.map((row) => formatInlineSkillRun(row)));
  if (report.page?.hasMore && report.page.nextCursor) {
    lines.push(`more: hasMore=true nextCursor=${report.page.nextCursor}`);
  }
  if (report.nextCommands.length > 0) {
    lines.push("next:");
    lines.push(...report.nextCommands.map((command) => `  ${command}`));
  }
  return lines;
}

function formatRunSummaryPage(page: RunSummaryPage | null | undefined) {
  const lines: unknown[] = (page?.items ?? []).map(formatRunListRow);
  if (page?.page.hasMore && page.page.nextCursor) {
    lines.push(`more: hasMore=true nextCursor=${page.page.nextCursor}`);
  }
  return lines;
}

function formatInlineSkillRun(row: RunListRow) {
  const runId = formatCliRunId(runIdOf(row));
  const issue = formatIssueRef(row.issue);
  const label = row.skillEvidence?.matchedSkillLabel && row.skillEvidence.matchedSkillLabel !== row.skillEvidence.matchedSkillKey
    ? ` label=${row.skillEvidence.matchedSkillLabel}`
    : "";
  return [
    `id=${runId}`,
    `status=${runStatusOf(row)}`,
    `agent=${row.agentName ?? formatCliAgentId(runAgentIdOf(row))}`,
    `issue=${issue}`,
    `runtime=${runRuntimeOf(row)}`,
    `createdAt=${runCreatedAtOf(row)}`,
    `finishedAt=${runFinishedAtOf(row) ?? "-"}`,
    `evidence=${row.skillEvidence?.evidenceType ?? "-"}`,
    `skill=${row.skillEvidence?.matchedSkillKey ?? "-"}${label}`,
    `error=${runErrorOf(row) ?? "-"}`,
    `next=${runStatusOf(row) === "failed" ? `rudder runs errors ${runId}` : `rudder runs transcript ${runId}`}`,
  ].join(" ");
}

function formatIssueRef(issue: RunExportRow["issue"] | RunSummary["issue"]) {
  if (!issue) return "-";
  return issue.identifier && issue.title ? `${issue.identifier} ${issue.title}` : issue.identifier ?? issue.title ?? issue.id;
}

function isRunSummary(row: RunListRow): row is RunSummary {
  return "id" in row;
}

function runIdOf(row: RunListRow) {
  return isRunSummary(row) ? row.id : row.run.id;
}

function runAgentIdOf(row: RunListRow) {
  return isRunSummary(row) ? row.agentId : row.run.agentId;
}

function runStatusOf(row: RunListRow) {
  return isRunSummary(row) ? row.status : row.run.status;
}

function runRuntimeOf(row: RunListRow) {
  return isRunSummary(row) ? row.runtime : row.bundle.agentRuntimeType;
}

function runCreatedAtOf(row: RunListRow) {
  return isRunSummary(row) ? row.createdAt : row.run.createdAt;
}

function runFinishedAtOf(row: RunListRow) {
  return isRunSummary(row) ? row.finishedAt : row.run.finishedAt;
}

function runErrorOf(row: RunListRow) {
  return isRunSummary(row) ? row.error : row.errorSummary;
}

function formatRunEvent(row: HeartbeatRunEvent) {
  return {
    seq: row.seq,
    level: row.level,
    stream: row.stream,
    message: row.message,
    createdAt: row.createdAt,
  };
}

function formatRunTranscriptRow(row: RunTranscriptRow) {
  return {
    id: row.id,
    turn: row.turnIndex ?? "-",
    kind: row.kind,
    ts: row.ts,
    error: row.isError ? "yes" : "no",
    preview: row.preview || row.detailPreview,
    ...(row.output ? { output: row.output.text } : {}),
  };
}

function formatRunTranscriptNarrative(row: RunTranscriptRow) {
  const marker = row.isError ? "ERROR " : "";
  return `${row.id} ${row.ts} ${marker}${row.kind}: ${row.preview || row.detailPreview}${row.output ? `\n${row.output.text}` : ""}`;
}

function formatRunError(row: RunErrorRow) {
  return {
    id: row.id,
    type: row.type,
    turn: row.turnIndex ?? "-",
    summary: row.summary,
    output: row.output.text,
    context: row.transcriptContext?.command ?? "-",
  };
}

function parseLimit(value: string | undefined, fallback: number) {
  const parsed = Number(value ?? fallback);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.floor(parsed);
}

function parseNonNegativeInteger(value: string | undefined, fallback: number) {
  const parsed = Number(value ?? fallback);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return Math.floor(parsed);
}

function clip(value: string, maxChars: number) {
  if (value.length <= maxChars) return value;
  return `${value.slice(0, Math.max(0, maxChars - 1))}…`;
}
